import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import type { AdminMembersResponseDto } from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import { fetchAdminMembers } from "../../shared/api/adminDirectory";
import { createFamilyAccount } from "../../shared/api/adminRoster";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { getProtectedApiErrorMessage, isUnauthenticatedApiError } from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";
import { buildAdminRosterLinks } from "./AdminRosterListShared";
import {
  buildCreateFamilyAccountRequest,
  createInitialFamilyAccountFormState,
  FamilyAccountCreateForm,
  type FamilyAccountFormState
} from "./AdminRosterForms";

export function AdminFamilyAccountCreatePage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [members, setMembers] = useState<AdminMembersResponseDto | null>(null);
  const [familyAccountForm, setFamilyAccountForm] = useState<FamilyAccountFormState>(createInitialFamilyAccountFormState());
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) return;
    setLoadState("loading");
    fetchAdminMembers(tenantSlug)
      .then((nextMembers) => {
        setMembers(nextMembers);
        setLoadState("idle");
      })
      .catch((error) => {
        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }
        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "メンバーの取得に失敗しました"));
      });
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  async function handleCreateFamilyAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setLoadError(null);
    setFeedbackMessage(null);

    if (!familyAccountForm.displayName.trim()) {
      setBusy(false);
      setLoadError(`${tenantLabels.familyAccount}名を入力してください。`);
      return;
    }

    try {
      const created = await createFamilyAccount(tenantSlug, buildCreateFamilyAccountRequest(familyAccountForm));
      const message = `${tenantLabels.familyAccount}を作成しました: ${created.displayName}`;
      setFeedbackMessage(message);
      navigate(`/t/${tenantSlug}/teacher/admin/family-accounts/${created.familyAccountId}`, {
        replace: true,
        state: { adminFeedbackMessage: message }
      });
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.familyAccount}の作成に失敗しました`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={`${tenantLabels.familyAccount}登録`}
      description={`${tenantLabels.familyAccount}を登録します。代表保護者は未設定でも作成できます。`}
      links={buildAdminRosterLinks(tenantLabels)}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>メンバーを読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}
        {isSessionReady && hasAdminRole ? (
          <FamilyAccountCreateForm
            form={familyAccountForm}
            members={members}
            tenantLabels={tenantLabels}
            busy={busy}
            onChange={setFamilyAccountForm}
            onSubmit={(event) => void handleCreateFamilyAccount(event)}
          />
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
